import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  OnInit,
} from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { SidebarComponent } from './core/layout/components/sidebar/sidebar.component';
import { ToolbarComponent } from './core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from './core/layout/components/footer/footer.component';
import { SessionNoticeComponent } from './core/project-session/components/session-notice/session-notice.component';
import { STAGES } from './core/layout/components/sidebar/sidebar.model';
import { ProjectSessionService } from './core/project-session/project-session.service';

/** Maps URL path to sidebar stage index. Update when new wizard routes are added. */
const URL_STAGE_MAP: Record<string, number> = {
  '/requirements-intake': 0,
  '/classification': 1,
  '/backlog-review': 2,
  '/ui-feedback': 3,
  '/architecture': 4,
  '/agentified-build': 5,
  '/quality-control': 6,
  '/costs-budget': 7,
  '/deploy': 8,
};

/**
 * AppComponent — Root application shell layout host.
 *
 * Sidebar collapse/expand is deferred to a future story.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarComponent,
    ToolbarComponent,
    FooterComponent,
    SessionNoticeComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly router = inject(Router);
  private readonly projectSession = inject(ProjectSessionService);

  activeStageIndex = signal<number>(0);

  /**
   * True while an /auth route is showing. Authentication is a full-screen
   * takeover: the sidebar, toolbar and footer are all workflow chrome, and
   * showing them behind a login the user cannot act on reads as a broken app.
   */
  readonly isAuthRoute = signal<boolean>(false);

  /**
   * Set of 0-based stage indices that have been confirmed/completed.
   * Passed to SidebarComponent as [completedIndices].
   *
   * Currently always empty — sidebar stage icons no longer turn green on
   * confirmation; stage advancement is handled via direct navigation instead.
   */
  completedStageIndices = signal<ReadonlySet<number>>(new Set<number>());

  /** True on a wizard stage route. Drives the sidebar's stage-flow nav — there is no "current stage" on Home. */
  readonly isWorkflowRoute = signal<boolean>(false);

  /** True on the Home route (`/`), where the toolbar title reads "Home" rather than a stage name. */
  readonly isHomeRoute = signal<boolean>(false);

  pageTitle = computed(() => (this.isHomeRoute() ? 'Home' : STAGES[this.activeStageIndex()].name));

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isAuthRoute.set(event.urlAfterRedirects.startsWith('/auth'));
        const normalized = this.normalizeStageUrl(event.urlAfterRedirects);
        this.isHomeRoute.set(normalized === '/');
        const idx = URL_STAGE_MAP[normalized];
        this.isWorkflowRoute.set(idx !== undefined);
        // TL-R1: only set active stage from URL if this stage is not already completed
        if (idx !== undefined && !this.completedStageIndices().has(idx)) {
          this.activeStageIndex.set(idx);
        }
        this.liveAnnouncer.announce(`Navigated to ${this.pageTitle()}`);
      });
  }

  onStageSelected(index: number): void {
    this.activeStageIndex.set(index);
    const routes: Record<number, string> = {
      0: '/requirements-intake',
      1: '/classification',
      2: '/backlog-review',
      3: '/ui-feedback',
      4: '/architecture',
      5: '/agentified-build',
      6: '/quality-control',
      7: '/costs-budget',
      8: '/deploy',
    };
    const target = routes[index];
    if (target) {
      this.router.navigate(this.projectSession.stageRoute(target));
    }
  }

  private normalizeStageUrl(url: string): string {
    const [path] = url.split(/[?#]/);
    return path.replace(/^\/projects\/[^/]+/, '') || '/';
  }
}
